import { Component } from '@angular/core';
import { ContentBlock, ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';

export interface Tab {
    id: string;
    label: string;
    blocks: ContentBlock[];
}

export interface TabGroupBlock extends ContentBlock {
    tabs: Tab[];
    initialTab?: number;
    disablePagination?: boolean;
    headerPosition?: 'above' | 'below';
}

@Component({
    template: `
        <mat-tab-group
            [preserveContent]="true"
            [dynamicHeight]="true"
            [headerPosition]="block.headerPosition"
            [disablePagination]="block.disablePagination"
            [selectedIndex]="block.initialTab ?? 0"
            >
            <mat-tab *ngFor="let tab of block.tabs; trackBy: tabId" [label]="tab.label">
                <tyt-content-view [blocks]="tab.blocks"></tyt-content-view>
            </mat-tab>
        </mat-tab-group>
    `
})
export class ViewComponent extends ContentBlockViewComponent<TabGroupBlock> {
    tabId(tab: Tab) { return tab.id; }
}

@Component({
    template: `
        <div class="field-row">
            <mat-form-field class="label" appearance="outline" floatLabel="always">
                <mat-label>Initial Tab</mat-label>
                <input type="number" matInput [(ngModel)]="block.initialTab" min="0" [max]="(block.tabs?.length ?? 1) - 1" placeholder="0" />
                <mat-hint>Start with tab '{{ block.tabs[block.initialTab ?? 0]?.label ?? '(None)' }}' visible</mat-hint>
            </mat-form-field>
            <div class="spacer"></div>
            <mat-button-toggle-group [(ngModel)]="block.headerPosition" aria-label="Header Position">
                <mat-button-toggle value="above">Tabs Above</mat-button-toggle>
                <mat-button-toggle value="below">Tabs Below</mat-button-toggle>
            </mat-button-toggle-group>
            <mat-checkbox [(ngModel)]="block.disablePagination"
                matTooltip="Disable only as a last resort"
                >Disable paging</mat-checkbox>
        </div>

        <mat-tab-group
            [preserveContent]="true"
            [dynamicHeight]="true"
            [headerPosition]="block.headerPosition"
            [disablePagination]="block.disablePagination"
            [(selectedIndex)]="selectedTab"
            >
            <mat-tab *ngFor="let tab of block.tabs; trackBy: tabId" [label]="tab.label">
                <div class="editor-layout" [class.bottom-tabs]="block.headerPosition === 'below'">
                    <div class="tab-settings">
                        <div>
                            <mat-icon>tab_unselected</mat-icon>
                            Tab
                        </div>
                        <div class="controls">
                            <button mat-icon-button (click)="addBefore(tab)" matTooltip="Add a tab before this one">
                                <mat-icon>add</mat-icon>
                            </button>
                            <button mat-icon-button (click)="moveLeft(tab)" matTooltip="Move this tab left" [disabled]="tabIndex(tab) === 0">
                                <mat-icon>chevron_left</mat-icon>
                            </button>
                            <mat-form-field class="label" appearance="outline" floatLabel="always">
                                <mat-label>Label</mat-label>
                                <input type="text" matInput [(ngModel)]="tab.label" />
                            </mat-form-field>
                            <button mat-icon-button (click)="remove(tab)" matTooltip="Remove this tab">
                                <mat-icon>close</mat-icon>
                            </button>
                            <button mat-icon-button (click)="moveRight(tab)" matTooltip="Move this tab right" [disabled]="tabIndex(tab) === block.tabs.length - 1">
                                <mat-icon>chevron_right</mat-icon>
                            </button>
                            <div class="spacer"></div>
                            <button mat-icon-button (click)="addAfter(tab)" matTooltip="Add a tab after this one">
                                <mat-icon>add</mat-icon>
                            </button>
                        </div>
                    </div>
                    <div class="content-editor">
                        <div>
                            <mat-icon>space_dashboard</mat-icon>
                            Content
                        </div>
                        <tyt-content-editor [enableNotices]="false" [blocks]="tab.blocks"></tyt-content-editor>
                    </div>
                </div>
            </mat-tab>
        </mat-tab-group>
    `,
    styles: [`
        .editor-layout {
            display: flex;
            flex-direction: column;
            gap: 1em;

            &.bottom-tabs {
                .tab-settings {
                    order: 2;
                }
            }
        }

        mat-form-field.label {
            min-width: 1px;
            flex-shrink: 1;
        }

        .tab-settings {
            border: 1px solid #4f587b;
            padding: 1em 0.5em;
            border-radius: 5px;
            background-color: #21232b;
            display: flex;
            flex-direction: column;
            gap: 0.5em;
        }

        .content-editor {
            display: flex;
            flex-direction: column;
            gap: 0.5em;
        }

        .controls {
            display: flex;
            align-items: baseline;
        }

        tyt-content-editor {
            display: block;
            border: 1px solid #4f587b;
            padding: 0.5em;

            mat-form-field.label {
                flex-grow: 1;
            }
        }
    `]
})
export class EditorComponent extends ContentBlockEditorComponent<TabGroupBlock> {

    ngOnInit() {
        this.selectedTab = this.block?.initialTab ?? 0;
    }

    selectedTab: number = 0;

    tabId(tab: Tab) { return tab.id; }
    remove(tab: Tab) {
        let index = this.block.tabs.indexOf(tab);
        if (index >= 0)
            this.block.tabs.splice(index, 1);
    }

    tabIndex(tab: Tab) {
        return this.block.tabs.indexOf(tab);
    }

    moveLeft(tab: Tab) {
        let index = this.block.tabs.indexOf(tab);
        if (index === 0)
            return;
        this.block.tabs.splice(index, 1);
        this.block.tabs.splice(index - 1, 0, tab);
        this.selectedTab = index - 1;
    }

    moveRight(tab: Tab) {
        let index = this.block.tabs.indexOf(tab);
        if (index >= this.block.tabs.length - 1)
            return;
        this.block.tabs.splice(index, 1);
        this.block.tabs.splice(index + 1, 0, tab);
        this.selectedTab = index + 1;
    }

    addBefore(tab: Tab) {
        let index = this.block.tabs.indexOf(tab);
        if (index >= 0) {
            this.block.tabs.splice(index, 0, {
                id: crypto.randomUUID(),
                label: 'Tab',
                blocks: []
            });
            this.selectedTab = index;
        }
    }

    addAfter(tab: Tab) {
        let index = this.block.tabs.indexOf(tab);
        if (index < 0)
            index = this.block.tabs.length - 1;
        this.block.tabs.splice(index + 1, 0, {
            id: crypto.randomUUID(),
            label: 'Tab',
            blocks: []
        });
        this.selectedTab = index + 1;
    }
}

export const TAB_GROUP_BLOCK_TYPE: ContentBlockType<TabGroupBlock> = {
    id: 'tab-group',
    label: 'Tab Group',
    icon: 'dashboard',
    template: {
        headerPosition: 'above',
        initialTab: 0,
        tabs: [
            {
                id: '701d3749-96e0-4219-84a8-b17c3ad694d8',
                label: '',
                blocks: []
            }
        ]
    },
    preview: {
        tabs: [
            {
                id: '701d3749-96e0-4219-84a8-b17c3ad694d8',
                label: 'Tab 1',
                blocks: []
            },
            {
                id: '701d3749-96e0-4219-84a8-b17c3ad694d8',
                label: 'Tab 2',
                blocks: []
            }
        ]
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
};
